import React, { useMemo } from "react";
import { Box, Chip as MuiChip } from "@material-ui/core";
import CopyIcon from "@material-ui/icons/FileCopy";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from "@material-ui/icons/Description";
import { copyToClipboard } from "../../../../utils";
import { useApp } from "../../../../AppProvider";
import { DOWNLOAD_TYPES, BULK_BUTTON_STYLES } from "../constants";

/**
 * Custom hook for generating MaterialTable actions
 * Includes copy to clipboard and bulk download actions for selected rows
 *
 * @param {Object} params
 * @param {Array} params.filteredData - Current filtered table data
 * @param {Array} params.columns - Table column definitions
 * @param {number} params.selectedCount - Number of selected rows
 * @param {Function} params.setSelectedRows - Setter for selected rows
 * @param {Function} params.openDateDialog - Opens the date picker dialog
 * @returns {Array} Array of MaterialTable action objects
 */
const useTableActions = ({
  filteredData,
  columns,
  selectedCount,
  setSelectedRows,
  openDateDialog,
}) => {
  const { doToast } = useApp();

  const actions = useMemo(
    () => [
      // Free action - always visible in toolbar
      {
        icon: CopyIcon,
        tooltip: "Copy Data",
        isFreeAction: true,
        onClick: () => {
          try {
            copyToClipboard(filteredData, columns, () =>
              doToast("success", "Data was copied to your clipboard.")
            );
          } catch (err) {
            doToast("error", err?.message ?? "Something went wrong");
          }
        },
      },
      // Selection action - DOCX bulk download
      {
        icon: () => (
          <MuiChip
            icon={<DescriptionIcon style={{ color: "white" }} />}
            label={`Download (${selectedCount}) as DOCX`}
            style={BULK_BUTTON_STYLES.docx}
          />
        ),
        tooltip: "Download Selected as DOCX",
        onClick: (event, rows) => {
          setSelectedRows(rows);
          openDateDialog(DOWNLOAD_TYPES.BULK_DOCX);
        },
      },
      // Selection action - PDF bulk download
      {
        icon: () => (
          <Box ml={1}>
            <MuiChip
              icon={<PictureAsPdfIcon style={{ color: "white" }} />}
              label={`Download (${selectedCount}) as PDF`}
              style={BULK_BUTTON_STYLES.pdf}
            />
          </Box>
        ),
        tooltip: "Download Selected as PDF",
        onClick: (event, rows) => {
          setSelectedRows(rows);
          openDateDialog(DOWNLOAD_TYPES.BULK_PDF);
        },
      },
    ],
    [
      filteredData,
      columns,
      selectedCount,
      setSelectedRows,
      openDateDialog,
      doToast,
    ]
  );

  return actions;
};

export default useTableActions;
